import { Component } from '@theme/component';

const UPDATE_EVENT = 'brs-scroller:update';

/**
 * Horizontal product scroller: tracks scroll position for the progress bar and
 * exposes page-by-page scrolling for brs-carousel-nav arrows in the same section.
 *
 * @typedef {object} ScrollerRefs
 * @property {HTMLElement} track - The scrolling row of cards.
 * @property {HTMLElement} thumb - The progress bar thumb.
 *
 * @extends {Component<ScrollerRefs>}
 */
class BrsScroller extends Component {
  requiredRefs = ['track', 'thumb'];

  /** @type {AbortController | undefined} */
  #listenerController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = new AbortController();
    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, {
      passive: true,
      signal: this.#listenerController.signal,
    });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this.refs.track);

    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  /** @returns {{ atStart: boolean, atEnd: boolean, scrollable: boolean }} */
  get state() {
    const { scrollLeft, scrollWidth, clientWidth } = this.refs.track;
    const position = Math.abs(scrollLeft);
    const scrollable = scrollWidth - clientWidth > 1;

    return {
      atStart: position <= 1,
      atEnd: position >= scrollWidth - clientWidth - 1,
      scrollable,
    };
  }

  /**
   * Scrolls one visible page backwards or forwards.
   * @param {1 | -1} direction - 1 for next, -1 for previous (in reading direction).
   */
  scrollPage(direction) {
    const { track } = this.refs;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: track.clientWidth * direction * (rtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #update() {
    const { track } = this.refs;
    const { scrollWidth, clientWidth, scrollLeft } = track;
    const { scrollable } = this.state;

    this.toggleAttribute('data-scrollable', scrollable);
    this.style.setProperty('--brs-scroller-ratio', String(scrollWidth ? clientWidth / scrollWidth : 1));
    this.style.setProperty('--brs-scroller-offset', String(clientWidth ? Math.abs(scrollLeft) / clientWidth : 0));

    this.dispatchEvent(new CustomEvent(UPDATE_EVENT, { bubbles: true, detail: this.state }));
  }
}

/**
 * Previous / next arrows for the brs-scroller in the same section, so the
 * arrows can sit in the section header instead of over the cards.
 *
 * @typedef {object} NavRefs
 * @property {HTMLButtonElement} previous - Scrolls back one page.
 * @property {HTMLButtonElement} next - Scrolls forward one page.
 *
 * @extends {Component<NavRefs>}
 */
class BrsCarouselNav extends Component {
  requiredRefs = ['previous', 'next'];

  /** @type {AbortController | undefined} */
  #listenerController;

  connectedCallback() {
    super.connectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = new AbortController();
    this.#section?.addEventListener(UPDATE_EVENT, this.#sync, { signal: this.#listenerController.signal });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = undefined;
  }

  /** The arrows' scope: an explicit data-brs-carousel-scope wrapper (for example in the cart drawer), else the section. */
  get #section() {
    return this.closest('[data-brs-carousel-scope]') ?? this.closest('.shopify-section');
  }

  get #scroller() {
    const scroller = this.#section?.querySelector('brs-scroller');
    return scroller instanceof BrsScroller ? scroller : null;
  }

  previous() {
    this.#scroller?.scrollPage(-1);
  }

  next() {
    this.#scroller?.scrollPage(1);
  }

  #sync = () => {
    const scroller = this.#scroller;
    const { previous, next } = this.refs;

    // Recommendations load after the page, so the arrows wait hidden until there is something to scroll.
    if (!scroller?.isConnected) {
      this.hidden = true;
      return;
    }

    const { atStart, atEnd, scrollable } = scroller.state;
    this.hidden = !scrollable;
    previous.disabled = atStart;
    next.disabled = atEnd;

    const trackId = scroller.refs.track.id;
    if (trackId) {
      previous.setAttribute('aria-controls', trackId);
      next.setAttribute('aria-controls', trackId);
    }
  };
}

if (!customElements.get('brs-scroller')) customElements.define('brs-scroller', BrsScroller);
if (!customElements.get('brs-carousel-nav')) customElements.define('brs-carousel-nav', BrsCarouselNav);
